import { Button, Checkbox } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import { useMemo, useState } from 'react';

type FieldOption = { key: string; value: string; label: string };

// Renders as an antd Form.Item child, so it receives `value` / `onChange`
// automatically from Form. The header (label, selected count, select all /
// clear all) is always visible; only the checkbox grid collapses. Form
// validation ("Please Select Fields") is rendered by the parent
// Form.Item, outside this component entirely, so it's never hidden.
//
// Built as a plain button + div (not antd Collapse) so the header layout
// is fully controlled by our own flexbox — no fighting antd's internal
// header markup/CSS for alignment.
const DetailFieldsCheckboxGrid = ({
  value = [],
  onChange,
  options,
}: {
  value?: string[];
  onChange?: (value: string[]) => void;
  options: FieldOption[];
}) => {
  const [expanded, setExpanded] = useState(true);

  const allValues = useMemo(() => options.map((o) => o.value), [options]);
  const selectedCount = value.length;
  const allSelected =
    allValues.length > 0 && selectedCount === allValues.length;

  const toggleValue = (val: string, checked: boolean) => {
    onChange?.(
      checked ? [...value, val] : value.filter((item) => item !== val)
    );
  };

  if (options.length === 0) {
    return (
      <div className="flex items-center px-md py-dm rounded-lg border-none">
        <span className="text-sm text-neutral-90">
          Select a rule plan to see available fields
        </span>
      </div>
    );
  }

  return (
    <div className="border rounded-lg  bg-neutral-10 overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        className="w-full flex flex-wrap items-center border-none justify-between gap-x-md gap-y-sm px-4 py-sm  text-left"
      >
        <DownOutlined
          className={`!text-neutral-90 !text-xs transition-transform duration-200 ${
            expanded ? 'rotate-180' : ''
          }`}
        />
        <div className="flex items-center gap-3 min-w-0 shrink-0">
          <span className="text-sm font-semibold text-neutral-500 whitespace-nowrap">
            Fields <span className="text-danger-500">*</span>
          </span>

          <Button
            type="link"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onChange?.(allSelected ? [] : allValues);
            }}
            className="!h-auto !p-0 !text-xs !font-semibold"
          >
            {allSelected ? 'Clear all' : 'Select all'}
          </Button>
        </div>

        <div className="flex items-center gap-sm shrink-0 ml-auto">
          <span
            className={`inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full text-xs font-semibold shrink-0 ${
              selectedCount > 0
                ? 'bg-primary-500 text-neutral-10'
                : 'bg-success-200 text-neutral-300'
            }`}
          >
            {selectedCount}
          </span>
          <span className="text-xs text-neutral-90 whitespace-nowrap">
            of {allValues.length} selected
          </span>
        </div>
      </button>

      {expanded ? (
        <div className="px-md py-md">
          <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-2.5">
            {options.map((opt) => {
              const selected = value.includes(opt.value);
              return (
                <label
                  key={opt.key}
                  className={`flex items-center gap-2.5 px-3.5 py-2.5 rounded-lg border text-sm cursor-pointer transition-all duration-150 ${
                    selected
                      ? 'bg-primary-50 border-primary-400 text-primary-400 shadow-[0_0_0_1px_rgba(59,130,246,0.35)]'
                      : 'bg-neutral-10 border-neutral-40 text-neutral-400 hover:border-primary-300 hover:bg-primary-50/40'
                  }`}
                >
                  <Checkbox
                    checked={selected}
                    onChange={(e) => toggleValue(opt.value, e.target.checked)}
                  />
                  <span className="truncate leading-none">{opt.label}</span>
                </label>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default DetailFieldsCheckboxGrid;
